import { OrderDescriptionComponent } from './modal/order-description-modal/order-description/order-description.component';
import { Component, inject, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../models/response';
import { OrderService } from '../../../services/order.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { RestaurantService } from '../../../services/restaurant.service';
import moment from 'moment-timezone';
import { injectUrlViewState } from '../../../utils/url-view-state.util';

type OrderStatusTab = 'all' | 'active' | 'completed' | 'archived';

/** The order statuses each tab reads, as the list API's CSV. */
const STATUS_QUERY: Record<OrderStatusTab, string> = {
  all: '',
  active: 'ready_to_order,in_progress,ready_for_payment,payment_option_selected,payment_completed',
  completed: 'completed,cancelled',
  archived: 'archived',
};

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule, MatProgressBarModule],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.css',
})
export class OrdersComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scrollSentinel', { static: false }) scrollSentinel!: ElementRef;
  restaurantService = inject(RestaurantService);
  currentRestaurant: any;
  restaurantTimezone: any;
  orderService = inject(OrderService);
  ngxSpinnerService = inject(NgxSpinnerService);
  isOpen = false;
  selectedHours: any = 10000;
  displayOrders: any = [];
  responseOrders: any = [];
  selectedOrder: any;
  selectedTimeRange: string | null = '3';
  groupByCustomerList: { [key: number]: any[] } = {};
  lastEvaluateKey: string = '';
  itemsPerPage = 30;
  currentPage = 1;
  totalOrders = 100000;
  disableNextBtn: boolean = false;
  pageWithEvaluateKeys: any = [];
  selectedStatus: string = '';
  selectedStatusLabel: string = 'all';
  displayTimeRange: string = '3 Hours';
  displayOrderType: string = 'All';
  selectedOrderType: string = '';
  timeRanges: any = [
    { value: 3, label: '3 Hours' },
    { value: 12, label: '12 Hours' },
    { value: 24, label: '1 Day' },
    { value: 120, label: '5 Days' },
    { value: 168, label: '1 Week' },
    { value: 720, label: '1 Month' },
  ];
  orderTypes: any = [
    { value: '', label: 'All' },
    { value: 'dine_in', label: 'DineIn' },
    { value: 'togo', label: 'ToGo' },
    { value: 'togo_dine_in', label: 'ToGo DineIn' },
    { value: 'online', label: 'Online' },
  ];
  displayLoading: boolean = false;
  loadingMore: boolean = false;
  skeletonPlaceholders: number[] = Array(6).fill(0);
  private scrollObserver?: IntersectionObserver;
  private isLoadingMore: boolean = false;
  /** Status tab, order type and time range live in the URL (?status, ?type,
   * ?hours), so a browser reload reopens the same list. */
  private urlState = injectUrlViewState();

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) {
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.currentRestaurant = restaurant;
      this.restaurantTimezone = this.currentRestaurant?.time_zone;
    });
  }

  ngOnInit() {
    this.restoreFiltersFromUrl();
    this.getOrdersByHours(
      this.itemsPerPage,
      this.lastEvaluateKey,
      this.selectedStatus,
      this.selectedOrderType,
      false,
    );
  }

  ngAfterViewInit() {
    // Setup scroll observer after view initializes
    setTimeout(() => {
      this.setupScrollObserver();
    }, 500);
  }

  ngOnDestroy() {
    // Clean up observer when component is destroyed
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  }

  setupScrollObserver() {
    // Disconnect existing observer if any
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }

    const options = {
      root: null, // Use viewport as root
      rootMargin: '100px', // Start loading 100px before reaching the bottom
      threshold: 0.1,
    };

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // When sentinel element is visible and conditions are met, load more
        if (
          entry.isIntersecting &&
          !this.isLoadingMore &&
          !this.disableNextBtn &&
          !this.displayLoading &&
          this.lastEvaluateKey
        ) {
          console.log('Scroll sentinel detected, loading more orders...');
          this.loadMoreOrders();
        }
      });
    }, options);

    // Start observing the sentinel element
    this.observeSentinel();
  }

  observeSentinel() {
    // Try to observe the sentinel element
    setTimeout(() => {
      if (this.scrollSentinel?.nativeElement && this.scrollObserver) {
        try {
          this.scrollObserver.observe(this.scrollSentinel.nativeElement);
          console.log('Scroll sentinel is now being observed');
        } catch (error) {
          console.error('Error observing sentinel:', error);
        }
      } else {
        console.warn('Scroll sentinel element not found');
      }
    }, 100);
  }

  loadMoreOrders() {
    // Prevent multiple simultaneous requests
    if (this.isLoadingMore || this.disableNextBtn || !this.lastEvaluateKey) {
      console.log('Load more prevented:', {
        isLoadingMore: this.isLoadingMore,
        disableNextBtn: this.disableNextBtn,
        lastEvaluateKey: this.lastEvaluateKey,
      });
      return;
    }

    console.log('Loading more orders with key:', this.lastEvaluateKey);
    this.isLoadingMore = true;
    this.loadingMore = true;
    this.getOrdersByHours(
      this.itemsPerPage,
      this.lastEvaluateKey,
      this.selectedStatus,
      this.selectedOrderType,
      true,
    );
  }

  getOrdersByHours(
    pageLimit: number,
    lastEvaluateKey: string,
    status: string,
    orderType: string,
    append: boolean = false,
  ) {
    if (!append) {
      this.displayLoading = true;
    }

    this.orderService
      .getAllOrdersByTime(this.selectedTimeRange, pageLimit, lastEvaluateKey, status, orderType)
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            this.displayLoading = false;
            this.loadingMore = false;
            this.isLoadingMore = false;
            return;
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
            this.displayLoading = false;
            this.loadingMore = false;
            this.isLoadingMore = false;
            return;
          }

          const newOrders = response.data.orders;
          this.setOrdersDisplayStatus(newOrders);

          if (append) {
            // Append new orders to existing list (infinite scroll)
            this.displayOrders = [...this.displayOrders, ...newOrders];
            // Sort orders by order_number in descending created_at
            this.displayOrders.sort(
              (a: any, b: any) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            );
          } else {
            // Replace orders (initial load or filter change)
            this.displayOrders = newOrders;
            // Sort orders by order_number in descending created_at
            this.displayOrders.sort(
              (a: any, b: any) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            );
          }

          // Update pagination state
          if (response.data.last_evaluated_key) {
            this.disableNextBtn = false;
            this.lastEvaluateKey = response.data.last_evaluated_key;
          } else {
            this.disableNextBtn = true;
            this.lastEvaluateKey = '';
          }

          if (!this.pageWithEvaluateKeys.find((f: any) => f.page == this.currentPage)) {
            this.pageWithEvaluateKeys.push({
              page: this.currentPage,
              lastEvaluateKey: this.lastEvaluateKey,
            });
          }

          this.displayLoading = false;
          this.loadingMore = false;
          this.isLoadingMore = false;

          // Re-observe sentinel after new data loads (both initial and append)
          // This ensures the observer is active after data loads
          setTimeout(() => {
            this.observeSentinel();
          }, 300);
        },
        error: () => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
          this.displayLoading = false;
          this.loadingMore = false;
          this.isLoadingMore = false;
        },
      });
  }

  setOrdersDisplayStatus(orders: any[]) {
    if (orders && orders.length > 0) {
      orders.forEach((order: any) => {
        if (order.status == 'ready_to_order') {
          order.displayStatus = 'Ready to order';
        } else if (order.status == 'in_progress') {
          order.displayStatus = 'In progress';
        } else if (order.status == 'ready_for_payment') {
          order.displayStatus = 'Ready for payment';
        } else if (order.status == 'payment_option_selected') {
          order.displayStatus = 'Payment selected';
        } else if (order.status == 'payment_completed') {
          order.displayStatus = 'Payment completed';
        } else if (order.status == 'completed') {
          order.displayStatus = 'Completed';
        } else if (order.status == 'cancelled') {
          order.displayStatus = 'Cancelled';
        }
      });
    }
  }

  get paginatedItems() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.displayOrders.slice(startIndex, startIndex + this.itemsPerPage);
  }

  // get totalPages() {
  //   return Math.ceil(this.totalOrders / this.itemsPerPage);
  // }

  changePage(page: number) {
    this.currentPage = page;
    this.disableNextBtn = false;
    this.isLoadingMore = false;
    if (this.pageWithEvaluateKeys.find((f: any) => f.page == this.currentPage)) {
      if (this.currentPage == 1) {
        this.lastEvaluateKey = '';
        this.getOrdersByHours(
          this.itemsPerPage,
          this.lastEvaluateKey,
          this.selectedStatus,
          this.selectedOrderType,
          false,
        );
      } else {
        this.getOrdersByHours(
          this.itemsPerPage,
          this.pageWithEvaluateKeys.find((f: any) => f.page == this.currentPage - 1)
            .lastEvaluateKey,
          this.selectedStatus,
          this.selectedOrderType,
          false,
        );
      }
    } else {
      this.getOrdersByHours(
        this.itemsPerPage,
        this.lastEvaluateKey,
        this.selectedStatus,
        this.selectedOrderType,
        false,
      );
    }
  }

  changeItemsPerPage(count: number) {
    this.itemsPerPage = count;
    this.currentPage = 1;
    this.pageWithEvaluateKeys = [];
    this.responseOrders = [];
    this.lastEvaluateKey = '';
    this.isLoadingMore = false;
    if (this.pageWithEvaluateKeys.find((f: any) => f.page == this.currentPage)) {
      this.getOrdersByHours(
        this.itemsPerPage,
        this.pageWithEvaluateKeys.find((f: any) => f.page == this.currentPage).lastEvaluateKey,
        this.selectedStatus,
        this.selectedOrderType,
        false,
      );
    } else {
      this.getOrdersByHours(
        this.itemsPerPage,
        this.lastEvaluateKey,
        this.selectedStatus,
        this.selectedOrderType,
        false,
      );
    }
  }

  getOrderById(id: any) {
    this.ngxSpinnerService.show();
    this.orderService.getOrderById(id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.selectedOrder = response.data;
        this.addOpenOrderDescriptionModal(this.selectedOrder);
        this.ngxSpinnerService.hide();
        this.isOpen = true;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  addOpenOrderDescriptionModal(order?: any) {
    const dialogRef = this.dialog.open(OrderDescriptionComponent, {
      data: { selectedOrder: order, restaurantTimezone: this.restaurantTimezone },
      width: '460px',
      height: '100vh',
      position: { top: '1px', right: '0px', bottom: '3px' },
      panelClass: 'order-description-dialog',
      autoFocus: 'dialog',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
      }
    });
  }

  getCustomerName(id: any) {
    let findCustomer = this.selectedOrder.customers.find((f: any) => f.user_id == id);
    return findCustomer.first_name;
  }

  groupByCustomer(array: any[]) {
    return array.reduce(
      (result, currentCustomer) => {
        if (!result[currentCustomer.variation.ordered_by]) {
          result[currentCustomer.variation.ordered_by] = [];
        }
        result[currentCustomer.variation.ordered_by].push(currentCustomer);
        return result;
      },
      {} as Record<number, any[]>,
    );
  }

  toggleSidenav(id: any) {
    // this.isOpen = !this.isOpen;
    // this.isOpen = false;
    if (!this.isOpen) {
      this.getOrderById(id);
      // document.body.classList.add('body-overlay');
    } else {
      document.body.classList.remove('body-overlay');
      this.isOpen = false;
    }
  }

  /** The status tab, order type and time range the URL asks for, if valid. */
  private restoreFiltersFromUrl() {
    const status = this.urlState.pick<OrderStatusTab>(
      'status',
      ['all', 'active', 'completed', 'archived'],
      'all',
    );
    this.selectedStatusLabel = status;
    this.selectedStatus = STATUS_QUERY[status];

    const type = this.orderTypes.find((t: any) => t.value === this.urlState.get('type'));
    if (type) {
      this.selectedOrderType = type.value;
      this.displayOrderType = type.label;
    }

    const range = this.timeRanges.find((r: any) => String(r.value) === this.urlState.get('hours'));
    if (range) {
      this.selectedTimeRange = range.value;
      this.displayTimeRange = range.label;
      this.selectedHours = range.value;
    }
  }

  /** Back to page one and a fresh read with the current filters. */
  private reload() {
    this.currentPage = 1;
    this.pageWithEvaluateKeys = [];
    this.responseOrders = [];
    this.lastEvaluateKey = '';
    this.isLoadingMore = false;
    this.getOrdersByHours(
      this.itemsPerPage,
      this.lastEvaluateKey,
      this.selectedStatus,
      this.selectedOrderType,
      false,
    );
    // The sentinel is re-created with the list; observe the new one.
    setTimeout(() => {
      this.setupScrollObserver();
    }, 500);
  }

  filterOrders(status: OrderStatusTab) {
    this.selectedStatus = STATUS_QUERY[status];
    this.selectedStatusLabel = status;
    this.urlState.set('status', status, 'all');
    this.reload();
  }

  onTimeRangeChange(timeRange: any): void {
    this.selectedTimeRange = timeRange.value;
    this.displayTimeRange = timeRange.label;
    this.selectedHours = this.selectedTimeRange;
    this.urlState.set('hours', timeRange.value, this.timeRanges[0].value);
    this.reload();
  }

  onOrdertypeChange(type: any) {
    this.selectedOrderType = type.value;
    this.displayOrderType = type.label;
    this.urlState.set('type', type.value, '');
    this.reload();
  }

  closeSidenav() {
    document.body.classList.remove('body-overlay');
    this.isOpen = false;
  }

  getFirstLetterOfString(str: string) {
    if (str && str.length > 0) {
      return str.charAt(0).toUpperCase();
    }
    return '';
  }

  // The card shows this many customer avatars, then a "+N" badge whose tooltip
  // names the rest. The order details modal lists everyone.
  readonly maxCardCustomers = 3;

  hiddenCustomerNames(order: any): string {
    return (order.customers ?? [])
      .slice(this.maxCardCustomers)
      .map((customer: any) => customer.first_name)
      .filter(Boolean)
      .join(', ');
  }

  // Customer photo when one is set and it loads; otherwise the initial badge.
  private failedCustomerImages = new Set<string>();

  hasCustomerImage(customer: any): boolean {
    const url = customer?.img_path?.trim();
    return !!url && !this.failedCustomerImages.has(url);
  }

  onCustomerImageError(customer: any): void {
    this.failedCustomerImages.add(customer.img_path.trim());
  }

  truncateString(input: string, maxLength: number) {
    return input.length > maxLength ? input.slice(0, maxLength) + ' ... ' : input;
  }

  getRgbaColor(hexColor: string | undefined, opacity: number): string {
    if (!hexColor) return 'rgba(0, 0, 0, 0)'; // Default to transparent if color is undefined
    // Convert hex to RGB
    let r: number, g: number, b: number;

    if (hexColor.startsWith('#')) {
      const bigint = parseInt(hexColor.slice(1), 16);
      r = (bigint >> 16) & 255;
      g = (bigint >> 8) & 255;
      b = bigint & 255;
    } else if (hexColor.startsWith('rgb')) {
      // If the color is already in RGB format
      const rgbValues = hexColor.match(/\d+/g);
      if (!rgbValues) return 'rgba(0, 0, 0, 0)';
      [r, g, b] = rgbValues.map(Number);
    } else {
      return hexColor; // Fallback to original color if format is unsupported
    }

    // Return RGBA color with the desired opacity
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  getEmptyStateMessage(): string {
    switch (this.selectedStatusLabel) {
      case 'all':
        return 'No Orders Yet';
      case 'active':
        return 'No Active Orders';
      case 'completed':
        return 'No Completed Orders Yet';
      case 'archived':
        return 'No Archived Orders';
      default:
        return 'No Orders Yet';
    }
  }

  getEmptyStateDescription(): string {
    switch (this.selectedStatusLabel) {
      case 'all':
        return "When customers place orders, they'll appear here.";
      case 'active':
        return 'All caught up! Active orders will show here when customers place them.';
      case 'completed':
        return 'Orders that have been completed or cancelled will appear in this section.';
      case 'archived':
        return 'Archived orders will be stored here for future reference.';
      default:
        return "When customers place orders, they'll appear here.";
    }
  }

  onServedByImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/common/user-circle.svg';
  }

  displayCreateddate(created_at: string) {
    if (created_at) {
      return moment(created_at).tz(this.restaurantTimezone)?.format('DD MMM, YYYY HH:mm') || '';
    } else {
      return 'N/A';
    }
  }
}
